import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, inr, fdate } from "@/api";
import { PageHeader, EmptyState, SlabBadge } from "@/components/Shared";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Plus, Receipt, Printer, Ban, Pencil, Trash2 } from "lucide-react";

export default function Invoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const cancel = async (i) => {
    if (!window.confirm(`Cancel invoice ${i.number}? Stock will be restored and the client ledger credited ${inr(i.grand_total)}.`)) return;
    try {
      await api.post(`/invoices/${i.invoice_id}/cancel`);
      toast.success(`${i.number} cancelled — stock restored, ledger reversed`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Cancellation failed");
    }
  };

  const remove = async (i) => {
    if (!window.confirm(`Permanently DELETE invoice ${i.number}? Stock will be restored and the client ledger reversed. This removes the record entirely — prefer Cancel if you need an audit trail on the invoice itself.`)) return;
    try {
      await api.delete(`/invoices/${i.invoice_id}`);
      toast.success(`${i.number} deleted — stock restored, ledger reversed`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Tax Invoices" sub="GST invoices with auto slab pricing · editable, backdatable within the active FY, printable with company logo">
        <Link to="/app/invoices/new">
          <Button data-testid="new-invoice-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
            <Plus className="w-4 h-4 mr-1" /> New Tax Invoice
          </Button>
        </Link>
      </PageHeader>

      {invoices.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" sub="Raise your first GST tax invoice" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="invoices-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Invoice #</th><th className="px-5 py-3">Client</th>
                <th className="px-5 py-3">Slab</th><th className="px-5 py-3">Date</th>
                <th className="px-5 py-3 text-right">Taxable</th><th className="px-5 py-3 text-right">GST</th>
                <th className="px-5 py-3 text-right">Total</th><th className="px-5 py-3 text-right">Balance Due</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {invoices.map((i) => (
                <tr key={i.invoice_id} className={`transition-colors duration-150 ${i.status === "cancelled" ? "opacity-50" : "hover:bg-slate-50"}`} data-testid={`invoice-row-${i.invoice_id}`}>
                  <td className="px-5 py-3.5 font-mono2 font-semibold text-slate-900">
                    {i.number}
                    {i.status === "cancelled" && (
                      <span className="ml-2 px-2 py-0.5 rounded-full border border-red-500/40 text-red-600 bg-red-500/10 text-[10px] font-mono2 uppercase" data-testid={`invoice-cancelled-badge-${i.invoice_id}`}>Cancelled</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-slate-800">{i.client.name}</td>
                  <td className="px-5 py-3.5"><SlabBadge slab={i.slab} /></td>
                  <td className="px-5 py-3.5 text-slate-600">{fdate(i.date)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 text-slate-700">{inr(i.subtotal)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 text-blue-600">{inr(i.gst_total)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 font-bold text-emerald-600">{inr(i.grand_total)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2" data-testid={`invoice-due-${i.invoice_id}`}>
                    {i.status === "cancelled" ? <span className="text-slate-400">—</span>
                      : (i.balance_due ?? i.grand_total) > 0
                        ? <span className="font-bold text-amber-600">{inr(i.balance_due ?? i.grand_total)}</span>
                        : <span className="px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-600 bg-emerald-500/10 text-[10px] font-mono2 uppercase">Paid</span>}
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <Link to={`/invoice/${i.invoice_id}/print`} data-testid={`invoice-print-${i.invoice_id}`}
                      className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                      <Printer className="w-3.5 h-3.5" /> View / Print
                    </Link>
                    {i.status !== "cancelled" && (
                      <Link to={`/app/invoices/${i.invoice_id}/edit`} data-testid={`invoice-edit-${i.invoice_id}`}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-500/10 transition-colors duration-150">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </Link>
                    )}
                    {user?.role === "admin" && i.status !== "cancelled" && (
                      <button onClick={() => cancel(i)} data-testid={`invoice-cancel-${i.invoice_id}`}
                        className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 px-2 py-1 rounded-lg hover:bg-amber-500/10 transition-colors duration-150">
                        <Ban className="w-3.5 h-3.5" /> Cancel
                      </button>
                    )}
                    {user?.role === "admin" && (
                      <button onClick={() => remove(i)} data-testid={`invoice-delete-${i.invoice_id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
